import { useEffect, useState } from "react"

import { HereGeocodePosition } from "../models/MapModels"
import { API_ROUTES } from "./Constants"
import { getHub } from "./Global"
import SocketContext from "./SocketContext"

/**
 * Where technicians are, right now, outside React.
 *
 * A company with a thousand technicians on the clock sends a point every time one of them
 * moves. Putting that in component state would re-render the map — and everything else holding
 * the technician bank — several times a second, to move a dot a few pixels. So the live
 * positions live here, in a plain map, and whoever needs them says so: the map moves its own
 * markers by hand, and the one drawer that happens to be open subscribes to the one technician
 * it is showing.
 *
 * The cost of a point arriving is then a map write and a callback for whatever is actually on
 * screen, rather than a render of everything that has ever heard of technicians.
 */

/** One technician's position as the hub sends it, inside a batch. */
export interface TechMapLocationUpdate {
    techId: string
    position: HereGeocodePosition
    at?: string
    /** The phone's battery with this point, 0–100 — absent from apps that do not send it. */
    batteryLevel?: number
    charging?: boolean
    /** "foreground" | "background": how the app was running when it sent this — absent from apps that do not say. */
    appState?: string
}

export interface TechLivePoint {
    position: HereGeocodePosition
    /** When the phone reported it, which is what "Live" and "5 minutes ago" are measured from. */
    at: Date
}

const points = new Map<string, TechLivePoint>()

const listeners = new Set<(techId: string, point: TechLivePoint) => void>()

/** The freshest position for a technician, or nothing if none has arrived this session. */
export const techLivePoint = (techId?: string): TechLivePoint | undefined =>
    techId ? points.get(techId) : undefined

export const applyTechLive = (techId?: string, position?: HereGeocodePosition, at?: Date | string) => {
    if (!techId || !position) return

    const point: TechLivePoint = {
        position,
        at: at ? new Date(at) : new Date()
    }

    points.set(techId, point)

    listeners.forEach(listener => {
        try {
            listener(techId, point)
        } catch {
            // One listener throwing must not stop the rest of the batch.
        }
    })
}

export const onTechLive = (listener: (techId: string, point: TechLivePoint) => void) => {
    listeners.add(listener)
    return () => { listeners.delete(listener) }
}

/** Signing out, or switching account: somebody else's technicians are not these. */
export const forgetTechLive = () => {
    points.clear()
    clocks.clear()
    deviceStates.clear()
}

/**
 * The live position of one technician, for a screen that is showing exactly one — the drawer.
 *
 * Deliberately not a hook over the whole map: subscribing to every technician would re-render
 * the drawer every time anybody in the company moved.
 */
export const useTechLive = (techId?: string): TechLivePoint | undefined => {
    const [point, pointSet] = useState<TechLivePoint | undefined>(() => techLivePoint(techId))

    useEffect(() => {
        pointSet(techLivePoint(techId))

        if (!techId) return

        return onTechLive((id, next) => {
            if (id === techId) pointSet(next)
        })
    }, [techId])

    return point
}

/* ---------------------------------------------------------------------------
   On the clock.

   A phone reporting is not the same as a technician working: the app sends its position in the
   background, so a fresh point alone would show somebody live all day. "Live" therefore needs
   both — on the clock, and a point within the last six minutes (or a clock-in that
   recent, while the first point is on its way). Off the clock they are offline, whatever the
   last point says.

   Loaded once from the hub, then kept current by "TechClockUpdate" on the socket. A reconnect
   loads it again, since whatever happened while the socket was down was never heard.
--------------------------------------------------------------------------- */

export interface TechClockState {
    on: boolean
    /** When they clocked in, or out. */
    at?: Date
}

/** Inside this, the phone is still reporting — it sends every 30 metres and at least every five
 * minutes standing still, so six minutes leaves room for one late report. */
// Three minutes while the apps report every two for testing (Farhad, 2026-10-18); six with
// the agreed five-minute reports.
export const techLiveWithinMs = 3 * 60 * 1000

const clocks = new Map<string, TechClockState>()

const clockListeners = new Set<(techId: string, state: TechClockState) => void>()

let clocksWatched = false

const setClock = (techId: string, state: TechClockState) => {
    clocks.set(techId, state)

    clockListeners.forEach(listener => {
        try {
            listener(techId, state)
        } catch {
            // As with points: one listener failing must not stop the others hearing it.
        }
    })
}

export const techClock = (techId?: string): TechClockState | undefined =>
    techId ? clocks.get(techId) : undefined

export const applyTechClock = (techId?: string, on?: boolean, at?: Date | string) => {
    if (!techId) return
    setClock(techId, { on: on === true, at: at ? new Date(at) : new Date() })
}

export const onTechClock = (listener: (techId: string, state: TechClockState) => void) => {
    clockListeners.add(listener)
    return () => { clockListeners.delete(listener) }
}

/** Everybody on the clock right now, as the hub lists them; everyone else is off it. */
const replaceTechClocks = (open: { techId?: string, since?: string }[]) => {
    const onNow = new Set(open.map(item => item.techId).filter((id): id is string => !!id))

    clocks.forEach((state, techId) => {
        if (state.on && !onNow.has(techId)) setClock(techId, { on: false, at: new Date() })
    })

    open.forEach(item => {
        if (!item.techId) return
        const known = clocks.get(item.techId)
        const since = item.since ? new Date(item.since) : undefined
        if (!known?.on || known.at?.getTime() !== since?.getTime()) setClock(item.techId, { on: true, at: since })
    })
}

/**
 * Starts following who is on the clock, once for the whole session. Safe to call from every
 * screen that shows a technician: only the first call does anything.
 */
export const watchTechClocks = () => {
    if (clocksWatched) return
    clocksWatched = true

    const load = () => {
        getHub().Get(API_ROUTES.Technicians + "clocks", res => {
            if (res.success) replaceTechClocks(res.data ?? [])
        })

        // What each technician's phone allows, for the location line in the map's panel and
        // drawer. Rides along with the clock: the same screens want both.
        getHub().Get(API_ROUTES.Technicians + "devicestates", res => {
            if (res.success) (res.data ?? []).forEach((item: TechDeviceState & { techId?: string }) => applyTechDeviceState(item.techId, item))
        })
    }

    SocketContext.instance.start(
        skt => {
            skt.off("TechClockUpdate")
            skt.on("TechClockUpdate", (techId: string, clockedIn: boolean, at?: string) => {
                applyTechClock(techId, clockedIn, at)
            })

            skt.off("TechDeviceStateUpdate")
            skt.on("TechDeviceStateUpdate", (techId: string, state: TechDeviceState) => {
                applyTechDeviceState(techId, state)
            })

            // Runs on every (re)connect: what changed while the socket was down was never heard.
            load()
        },
        skt => {
            skt.off("TechClockUpdate")
            skt.off("TechDeviceStateUpdate")
        }
    )

    load()
}

/**
 * Whether a technician is live: on the clock, and their phone reported (or they clocked in)
 * within the last six minutes.
 */
export const isTechLive = (techId?: string, reportedAt?: Date | string): boolean => {
    const clock = techClock(techId)
    if (!clock?.on) return false

    const reported = reportedAt ? new Date(reportedAt).getTime() : NaN
    const latest = Math.max(isNaN(reported) ? 0 : reported, clock.at?.getTime() ?? 0)

    return latest > 0 && Date.now() - latest < techLiveWithinMs
}

/** The clock of one technician, for a screen showing exactly one. */
export const useTechClock = (techId?: string): TechClockState | undefined => {
    const [state, stateSet] = useState<TechClockState | undefined>(() => techClock(techId))

    useEffect(() => {
        watchTechClocks()
        stateSet(techClock(techId))

        if (!techId) return

        return onTechClock((id, next) => {
            if (id === techId) stateSet(next)
        })
    }, [techId])

    return state
}

/* ---------------------------------------------------------------------------
   What each technician's phone allows — location access here; the rest is read
   on the technician's own page. Sent by the app every time it opens.
--------------------------------------------------------------------------- */

export interface TechDeviceState {
    /** "always" | "whileUsing" | "denied" | "notAsked" */
    locationStatus?: string
    locationPrecise?: boolean
    platform?: string
    reportedAt?: string
    /** 0–100, with the last location sent. */
    batteryLevel?: number
    charging?: boolean
    batteryAt?: string
    /** "foreground" | "background" | "closed": how the app is running, as it last said. */
    appState?: string
    appStateAt?: string
    /** "wifi" | "cellular" | "none" | "other": how the phone is online. */
    networkType?: string
    /** "5G" | "4G" | "3G" | "2G", on mobile data only. */
    networkGeneration?: string
    /** Bars, 0–4 — absent where the phone cannot tell (iPhones never can). */
    networkSignal?: number
    networkAt?: string
}

const deviceStates = new Map<string, TechDeviceState>()

const deviceStateListeners = new Set<(techId: string, state: TechDeviceState) => void>()

export const techDeviceState = (techId?: string): TechDeviceState | undefined =>
    techId ? deviceStates.get(techId) : undefined

export const applyTechDeviceState = (techId?: string, state?: TechDeviceState) => {
    if (!techId || !state) return

    // Merged rather than replaced: a permission report says nothing about the battery, and a
    // location's battery says nothing about permissions.
    const merged: TechDeviceState = { ...deviceStates.get(techId) }
    Object.entries(state).forEach(([key, value]) => {
        if (value !== undefined && value !== null) (merged as any)[key] = value
    })
    state = merged

    deviceStates.set(techId, state)

    deviceStateListeners.forEach(listener => {
        try {
            listener(techId, state)
        } catch {
            // As elsewhere: one listener failing must not stop the others.
        }
    })
}

export const onTechDeviceState = (listener: (techId: string, state: TechDeviceState) => void) => {
    deviceStateListeners.add(listener)
    return () => { deviceStateListeners.delete(listener) }
}

/** One technician's device state, for a screen showing exactly one. */
export const useTechDeviceState = (techId?: string): TechDeviceState | undefined => {
    const [state, stateSet] = useState<TechDeviceState | undefined>(() => techDeviceState(techId))

    useEffect(() => {
        watchTechClocks()
        stateSet(techDeviceState(techId))

        if (!techId) return

        return onTechDeviceState((id, next) => {
            if (id === techId) stateSet(next)
        })
    }, [techId])

    return state
}
